import React, {
    ReactElement,
} from "react";
import { Form } from "react-bootstrap";
import FieldType, { UserType } from "../../../../components/pickers/field-type/FieldType";
import { FormFieldType, WorkflowFormField } from "../../../../__generated__/api";
import ExpandingSection from "../../../../components/expanding-section/ExpandingSection";
import InputList from "./Input";
import FieldTypeValueDisplay from "../../../../components/form-field/FieldTypeValueDisplay";

type Json = Record<string, unknown>;

export function SchemaBuilderValue({
    json, onChange, keyName, title, rootJson,
}: {
    json : Json;
    onChange: (value: Json) => void;
    keyName: string;
    title: string;
    rootJson: Json;

}): ReactElement {
    return (
        <ExpandingSection title={title}>
            {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
            <SchemaBuilderValueFields
                rootJson={rootJson}
                onChange={value => onChange({
                    ...json,
                    [keyName]: value,
                })}
                json={json?.[keyName] as Json}
            />
        </ExpandingSection>
    );
}

export function SchemaBuilderValueFields({
    json, onChange, rootJson,
}: {
    json : Json;
    onChange: (value: Json) => void;
    rootJson: Json;

}): ReactElement {
    const type = json?.type;
    if (type && type !== "map" && type !== "array" && type !== "record") {
        return (
            <InputList
                maxItems={1}
                rootJson={rootJson}
                json={[json]}
                onChange={value => {
                    onChange(value.lastElement);
                }}
            />
        );
    }
    return (
        <div>
            {type != null && (
                <>
                    <Form.Group className="mb-2">
                        <Form.Label className="fw-bolder">Label</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Label"
                            required
                            onChange={evt => onChange({
                                ...json,
                                label: evt.target.value,
                            })}
                            value={json?.label as string ?? ""}
                        />
                        <Form.Text>
                            The label
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label className="fw-bolder">Description</Form.Label>
                        <Form.Control
                            type="text"
                            as="textarea"
                            placeholder="Description"
                            onChange={evt => onChange({
                                ...json,
                                description: evt.target.value,
                            })}
                            value={json?.description as string ?? ""}
                        />
                        <Form.Text>
                            The description
                        </Form.Text>
                    </Form.Group>
                </>
            )}
            <Form.Group className="mb-2">
                <Form.Label className="fw-bolder">Type</Form.Label>
                <FieldType
                    type={UserType.JSON}
                    className="text-gray"
                    onChange={value => onChange({
                        ...json,
                        type: value,
                    })}
                    value={type as FormFieldType}
                />

            </Form.Group>

            {json?.type === "map" && (
                <>
                    <SchemaBuilderValue rootJson={rootJson} title="Keys" keyName="keys" json={json} onChange={onChange} />
                    <SchemaBuilderValue rootJson={rootJson} title="Values" keyName="values" json={json} onChange={onChange} />

                </>
            )}
            {json?.type === "array" && (
                <SchemaBuilderValue rootJson={rootJson} title="Items" keyName="items" json={json} onChange={onChange} />
            )}
            {json?.type === "record" && (
                <InputList
                    rootJson={rootJson}
                    json={json.fields as Json[]}
                    onChange={fields => onChange({
                        ...json,
                        fields,
                    })}
                />
            )}

        </div>
    );
}

export default function SchemaBuilder({
    json, onChange, rootJson,
}: {
    json : Json;
    onChange: (value: Json) => void;
    rootJson: Json;

}): ReactElement {
    return (
        <div>
            <Form.Group className="mb-2">
                <Form.Label className="fw-bolder">Label</Form.Label>
                <Form.Control
                    type="text"
                    placeholder="Label"
                    required
                    onChange={evt => onChange({
                        ...json,
                        label: evt.target.value,
                    })}
                    value={json?.label as string ?? ""}
                />
                <Form.Text>
                    The label
                </Form.Text>
            </Form.Group>
            <Form.Group className="mb-2">
                <Form.Label className="fw-bolder">Description</Form.Label>
                <Form.Control
                    type="text"
                    as="textarea"
                    placeholder="Description"
                    required
                    onChange={evt => onChange({
                        ...json,
                        description: evt.target.value,
                    })}
                    value={json?.description as string ?? ""}
                />
                <Form.Text>
                    The description
                </Form.Text>
            </Form.Group>
            <SchemaBuilderValue rootJson={rootJson} title="Root Type" keyName="root" json={json} onChange={onChange} />

        </div>
    );
}
